import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Try admin first
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser({ ...data, role: data.role || 'admin' });
      setLoading(false);
      return;
    } catch {}
    // Try tenant portal
    try {
      const { data } = await axios.get(`${API_URL}/api/portal/me`, { withCredentials: true });
      setUser({ ...data, role: 'tenant' });
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    setUser({ ...data, role: data.role || 'admin' });
    return data;
  };

  const portalLogin = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/portal/login`, { email, password }, { withCredentials: true });
    setUser({ ...data, role: 'tenant' });
    return data;
  };

  const portalRegister = async (email, password, name, invite_code) => {
    const { data } = await axios.post(`${API_URL}/api/portal/register`, { email, password, name, invite_code }, { withCredentials: true });
    setUser({ ...data, role: 'tenant' });
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name }, { withCredentials: true });
    setUser({ ...data, role: data.role || 'admin' });
    return data;
  };

  const logout = async () => {
    const endpoint = user?.role === 'tenant' ? '/api/portal/logout' : '/api/auth/logout';
    try {
      await axios.post(`${API_URL}${endpoint}`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, portalLogin, portalRegister, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
